/**
 * Custom onboarding endpoints that are not generated by Orval
 * This file should not be overwritten by code generation
 */
import type {
  FindOrCreateTutorialDto,
  TutorialResponseDto,
  UpdateTutorialDto
} from './generated/schemas'
import { onboardingCustomInstance } from 'lib/api-instance'

export interface ChecklistStatusResponseDto {
  firstReading: boolean
  achievementArea: boolean
  plantTree: boolean
  readingPages: number
}

/**
 * Get checklist status for a user
 * @summary Get checklist status
 */
export type OnboardingControllerGetChecklistStatusResponse200 = {
  data: ChecklistStatusResponseDto
  status: 200
}

export type OnboardingControllerGetChecklistStatusResponse404 = {
  data: unknown
  status: 404
}

export type OnboardingControllerGetChecklistStatusResponseSuccess =
  OnboardingControllerGetChecklistStatusResponse200 & {
    headers: Headers
  }
export type OnboardingControllerGetChecklistStatusResponseError =
  OnboardingControllerGetChecklistStatusResponse404 & {
    headers: Headers
  }

export type OnboardingControllerGetChecklistStatusResponse =
  | OnboardingControllerGetChecklistStatusResponseSuccess
  | OnboardingControllerGetChecklistStatusResponseError

export const getOnboardingControllerGetChecklistStatusUrl = (
  userId: number
) => {
  return `/api-arvore/onboarding/checklist/${userId}`
}

export const onboardingControllerGetChecklistStatus = async (
  userId: number,
  options?: RequestInit
): Promise<OnboardingControllerGetChecklistStatusResponse> => {
  return onboardingCustomInstance<OnboardingControllerGetChecklistStatusResponse>(
    getOnboardingControllerGetChecklistStatusUrl(userId),
    {
      ...options,
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...options?.headers }
    }
  )
}

/**
 * Find or create tutorial endpoint (custom version for localhost:3001)
 * @summary Find or create tutorial
 */
export type OnboardingControllerFindOrCreateTutorialEndpointResponse200 = {
  data: TutorialResponseDto
  status: 201
}

export type OnboardingControllerFindOrCreateTutorialEndpointResponse400 = {
  data: unknown
  status: 400
}

export type OnboardingControllerFindOrCreateTutorialEndpointResponseSuccess =
  OnboardingControllerFindOrCreateTutorialEndpointResponse200 & {
    headers: Headers
  }
export type OnboardingControllerFindOrCreateTutorialEndpointResponseError =
  OnboardingControllerFindOrCreateTutorialEndpointResponse400 & {
    headers: Headers
  }

export type OnboardingControllerFindOrCreateTutorialEndpointResponse =
  | OnboardingControllerFindOrCreateTutorialEndpointResponseSuccess
  | OnboardingControllerFindOrCreateTutorialEndpointResponseError

export const getOnboardingControllerFindOrCreateTutorialEndpointUrl = () => {
  return `/api-arvore/onboarding/tutorials`
}

export const onboardingControllerFindOrCreateTutorialEndpoint = async (
  findOrCreateTutorialDto: FindOrCreateTutorialDto,
  options?: RequestInit
): Promise<OnboardingControllerFindOrCreateTutorialEndpointResponse> => {
  return onboardingCustomInstance<OnboardingControllerFindOrCreateTutorialEndpointResponse>(
    getOnboardingControllerFindOrCreateTutorialEndpointUrl(),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      body: JSON.stringify(findOrCreateTutorialDto)
    }
  )
}

/**
 * Update tutorial endpoint (custom version for localhost:3001)
 * @summary Update tutorial state
 */
export type OnboardingControllerUpdateTutorialEndpointResponse200 = {
  data: TutorialResponseDto
  status: 200
}

export type OnboardingControllerUpdateTutorialEndpointResponse400 = {
  data: unknown
  status: 400
}

export type OnboardingControllerUpdateTutorialEndpointResponse404 = {
  data: unknown
  status: 404
}

export type OnboardingControllerUpdateTutorialEndpointResponseSuccess =
  OnboardingControllerUpdateTutorialEndpointResponse200 & {
    headers: Headers
  }
export type OnboardingControllerUpdateTutorialEndpointResponseError =
  | (OnboardingControllerUpdateTutorialEndpointResponse400 & {
      headers: Headers
    })
  | (OnboardingControllerUpdateTutorialEndpointResponse404 & {
      headers: Headers
    })

export type OnboardingControllerUpdateTutorialEndpointResponse =
  | OnboardingControllerUpdateTutorialEndpointResponseSuccess
  | OnboardingControllerUpdateTutorialEndpointResponseError

export const getOnboardingControllerUpdateTutorialEndpointUrl = () => {
  return `/api-arvore/onboarding/tutorials`
}

export const onboardingControllerUpdateTutorialEndpoint = async (
  updateTutorialDto: UpdateTutorialDto,
  options?: RequestInit
): Promise<OnboardingControllerUpdateTutorialEndpointResponse> => {
  return onboardingCustomInstance<OnboardingControllerUpdateTutorialEndpointResponse>(
    getOnboardingControllerUpdateTutorialEndpointUrl(),
    {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      body: JSON.stringify(updateTutorialDto)
    }
  )
}

/**
 * Give rewards to a user
 * @summary Give rewards
 */
export type GiveRewardsDto = {
  userId: number
  coins?: number
  waterDrops?: number
  points?: number
}

export type RewardResponseDto = {
  id: number
  userId: number
  coins: number
  waterDrops: number
  points: number
  createdAt: string
}

export type OnboardingControllerGiveRewardsResponse200 = {
  data: RewardResponseDto
  status: 201
}

export type OnboardingControllerGiveRewardsResponse400 = {
  data: unknown
  status: 400
}

export type OnboardingControllerGiveRewardsResponse409 = {
  data: { message: string }
  status: 409
}

export type OnboardingControllerGiveRewardsResponseSuccess =
  OnboardingControllerGiveRewardsResponse200 & {
    headers: Headers
  }
export type OnboardingControllerGiveRewardsResponseError =
  | (OnboardingControllerGiveRewardsResponse400 & { headers: Headers })
  | (OnboardingControllerGiveRewardsResponse409 & { headers: Headers })

export type OnboardingControllerGiveRewardsResponse =
  | OnboardingControllerGiveRewardsResponseSuccess
  | OnboardingControllerGiveRewardsResponseError

export const getOnboardingControllerGiveRewardsUrl = () => {
  return `/api-arvore/onboarding/rewards`
}

export const onboardingControllerGiveRewards = async (
  giveRewardsDto: GiveRewardsDto,
  options?: RequestInit
): Promise<OnboardingControllerGiveRewardsResponse> => {
  const url = getOnboardingControllerGiveRewardsUrl()

  const response =
    await onboardingCustomInstance<OnboardingControllerGiveRewardsResponse>(
      url,
      {
        ...options,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: JSON.stringify(giveRewardsDto)
      }
    )

  return response
}
